import React from "react"
import {
  Loader2,
  StretchHorizontal,
  AlignCenter,
  Pencil,
  Code,
  Eye,
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react"
import { Button } from "../ui/button"
import { formatTimeAgo } from "../layout/document-viewer.utils"
import { testIds } from "@/lib/test-ids"
import { cn } from "@/lib/utils"
import { SHELL_COLUMN_FOOTER_CLASS } from "@/lib/shell-chrome"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"
import type { AsyncActivityState } from "@/components/ui/async-activity-strip"
import { GdocStatusBarMeta } from "@/components/integrations/google-workspace/gdoc-status-bar-meta"

/** Container width at which labels and inline stats appear (panel ~550px stays compact). */
const STATUS_BAR_WIDE_CQ = "@[620px]"

export type DocumentContentStatusBarProps = {
  viewMode: "wysiwyg" | "source"
  wysiwygEditorMounted: boolean
  localIsDeleted: boolean
  pageWidthMode: "default" | "full"
  /** Reading-column toggle only applies to Preview (TipTap), not Code. */
  showPageWidthToggle?: boolean
  /** Hide for custom renderers - Code mode is how you edit the markdown source. */
  showEditToggle?: boolean
  onSetViewMode: (mode: "wysiwyg" | "source") => void
  onSetWysiwygEditorMounted: (mounted: boolean) => void
  onTogglePageWidthMode: () => void
  localSaving: boolean
  hasUnsavedChanges: boolean
  lastSaved: Date | null
  lastFileSave: Date | null
  deletedMessage: string | null
  onRestore: () => void
  contentLineCount: number
  contentCharCount: number
  error: string | null
  /** Inline Drive sync status (replaces layout-shifting top banner). */
  driveSyncActivity?: AsyncActivityState
  driveSyncLabel?: string | null
  driveGdocId?: string | null
  driveOrganizationId?: string | null
  driveLastSynced?: string | null
  driveLastPushed?: string | null
  driveLastPulled?: string | null
  driveKeepSynced?: boolean
  /**
   * Always-visible (i) tooltip copy (product board gestures, etc.).
   * When set, the info control stays on the right at all widths - same hit target as compact (i).
   */
  infoTips?: string
}

function SaveStatusContent({
  localSaving,
  hasUnsavedChanges,
  lastSaved,
  lastFileSave,
}: Pick<
  DocumentContentStatusBarProps,
  "localSaving" | "hasUnsavedChanges" | "lastSaved" | "lastFileSave"
>) {
  if (localSaving) {
    return (
      <div className="flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin text-primary" />
        <span className="text-muted-foreground">Saving...</span>
      </div>
    )
  }

  if (hasUnsavedChanges) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="h-2 w-2 rounded-full bg-yellow-500" />
        <span className="text-yellow-600 dark:text-yellow-500">Unsaved changes</span>
      </div>
    )
  }

  if (lastSaved || lastFileSave) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="h-2 w-2 rounded-full bg-green-500" />
        <span className="text-muted-foreground">
          Saved {formatTimeAgo(lastSaved ?? lastFileSave!)}
        </span>
      </div>
    )
  }

  return null
}

function DocumentStats({
  contentLineCount,
  contentCharCount,
}: Pick<DocumentContentStatusBarProps, "contentLineCount" | "contentCharCount">) {
  return (
    <div className="flex items-center gap-2">
      <span>{contentLineCount.toLocaleString()} lines</span>
      <span aria-hidden>{"\u2022"}</span>
      <span>{contentCharCount.toLocaleString()} chars</span>
    </div>
  )
}

export function DocumentContentStatusBar({
  viewMode,
  wysiwygEditorMounted,
  localIsDeleted,
  pageWidthMode,
  showPageWidthToggle = true,
  showEditToggle = true,
  onSetViewMode,
  onSetWysiwygEditorMounted,
  onTogglePageWidthMode,
  localSaving,
  hasUnsavedChanges,
  lastSaved,
  lastFileSave,
  deletedMessage,
  onRestore,
  contentLineCount,
  contentCharCount,
  error,
  driveSyncActivity,
  driveSyncLabel,
  driveGdocId,
  driveOrganizationId,
  driveLastSynced,
  driveLastPushed,
  driveLastPulled,
  driveKeepSynced,
  infoTips,
}: DocumentContentStatusBarProps) {
  const pageWidthTitle =
    pageWidthMode === "default"
      ? "Switch to full width"
      : "Switch to reading column (760px)"

  const saveStatus = (
    <SaveStatusContent
      localSaving={localSaving}
      hasUnsavedChanges={hasUnsavedChanges}
      lastSaved={lastSaved}
      lastFileSave={lastFileSave}
    />
  )

  const driveMeta =
    driveGdocId ? (
      <GdocStatusBarMeta
        gdocId={driveGdocId}
        lastSynced={driveLastSynced}
        lastPushed={driveLastPushed}
        lastPulled={driveLastPulled}
        organizationId={driveOrganizationId}
        keepSynced={driveKeepSynced}
        activity={driveSyncActivity}
        activityLabel={driveSyncLabel}
      />
    ) : null

  const infoTooltipBody = (
    <>
      {infoTips ? (
        <p className="leading-relaxed text-foreground">{infoTips}</p>
      ) : null}
      {driveMeta}
      {saveStatus ?? (
        <p className="text-muted-foreground">No save status</p>
      )}
      <DocumentStats
        contentLineCount={contentLineCount}
        contentCharCount={contentCharCount}
      />
      {localIsDeleted && deletedMessage && (
        <p className="text-red-600">{deletedMessage}</p>
      )}
      {error && <p className="text-red-600">{error}</p>}
    </>
  )

  const infoButton = (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-center h-6 w-6 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          aria-label={infoTips ? "Board tips and document status" : "Document status and stats"}
          data-testid={testIds.documentContentStatusBar.infoButton}
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="end" className="max-w-sm space-y-1.5 text-xs">
        {infoTooltipBody}
      </TooltipContent>
    </Tooltip>
  )

  return (
    <div
      className={cn(
        "@container flex items-center justify-between gap-2 px-2 @[620px]:gap-3 @[620px]:px-4 border-t border-border bg-muted/30 text-xs min-w-0",
        SHELL_COLUMN_FOOTER_CLASS,
      )}
      data-testid={testIds.documentContentStatusBar.root}
    >
      <div className="flex items-center gap-1.5 @[620px]:gap-3 min-w-0 shrink-0">
        {viewMode === "wysiwyg" && !localIsDeleted && showEditToggle && (
          <button
            type="button"
            data-testid={testIds.documentContentStatusBar.editToggle}
            aria-pressed={wysiwygEditorMounted}
            aria-label={
              wysiwygEditorMounted
                ? "Stop editing (Esc)"
                : "Edit document (double-click also works)"
            }
            className={cn(
              "flex items-center justify-center gap-0 @[620px]:gap-1.5 px-1.5 @[620px]:px-2 py-1 text-xs transition-colors rounded shrink-0",
              wysiwygEditorMounted
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
            title={
              wysiwygEditorMounted
                ? "Stop editing (Esc)"
                : "Edit document (double-click also works)"
            }
            tabIndex={-1}
            onClick={() => onSetWysiwygEditorMounted(!wysiwygEditorMounted)}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            <span className={cn("hidden", STATUS_BAR_WIDE_CQ + ":inline")}>
              {wysiwygEditorMounted ? "Editing" : "Edit"}
            </span>
          </button>
        )}

        <div
          className="inline-flex items-center rounded-md border border-border p-0.5 shrink-0"
          role="tablist"
          aria-label="Document view mode"
          data-testid={testIds.documentContentStatusBar.viewModeTabs}
        >
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "wysiwyg"}
            aria-label="Preview mode"
            className={cn(
              "flex items-center justify-center gap-0 @[620px]:gap-1.5 px-1.5 @[620px]:px-2 py-1 text-xs rounded-sm transition-colors",
              viewMode === "wysiwyg"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
            title="Preview (rendered markdown; double-click or Edit to change prose)"
            tabIndex={-1}
            onClick={() => {
              onSetViewMode("wysiwyg")
            }}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
            <span className={cn("hidden", STATUS_BAR_WIDE_CQ + ":inline")}>Preview</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "source"}
            aria-label="Code mode"
            className={cn(
              "flex items-center justify-center gap-0 @[620px]:gap-1.5 px-1.5 @[620px]:px-2 py-1 text-xs rounded-sm transition-colors",
              viewMode === "source"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
            title="Code (line numbers, syntax, change gutter when edits are pending)"
            tabIndex={-1}
            onClick={() => {
              // Note: intentionally does NOT force wysiwygEditorMounted true here. Code mode
              // renders via CodeMirror and never reads that flag, so forcing it on this click
              // had no effect on Code itself, but it stuck around after switching back to
              // Preview - silently dropping the user into WYSIWYG edit mode (and showing the
              // Edit button as active) instead of the expected read-only preview.
              onSetViewMode("source")
            }}
          >
            <Code className="h-3.5 w-3.5" aria-hidden />
            <span className={cn("hidden", STATUS_BAR_WIDE_CQ + ":inline")}>Code</span>
          </button>
        </div>

        {showPageWidthToggle && viewMode === "wysiwyg" && (
          <button
            type="button"
            aria-label={pageWidthTitle}
            className="flex items-center justify-center gap-0 @[620px]:gap-1.5 px-1.5 @[620px]:px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors rounded shrink-0"
            title={pageWidthTitle}
            tabIndex={-1}
            onClick={onTogglePageWidthMode}
            data-testid={testIds.documentContentStatusBar.pageWidthToggle}
          >
            {pageWidthMode === "default" ? (
              <AlignCenter className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <StretchHorizontal className="h-3.5 w-3.5" aria-hidden />
            )}
            <span className={cn("hidden", STATUS_BAR_WIDE_CQ + ":inline")}>
              {pageWidthMode === "default" ? "Column" : "Full"}
            </span>
          </button>
        )}

      </div>

      <div className={cn("hidden items-center justify-center gap-3 shrink-0 min-w-0", STATUS_BAR_WIDE_CQ + ":flex")}>
        {driveMeta}
        {driveMeta && saveStatus ? (
          <span className="text-muted-foreground/50" aria-hidden>
            {"\u2022"}
          </span>
        ) : null}
        {saveStatus}
        {localIsDeleted && (
          <div className="flex items-center gap-2">
            <span className="text-red-600 flex items-center gap-1">
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              {deletedMessage}
            </span>
            <Button
              onClick={onRestore}
              size="sm"
              variant="outline"
              className="h-6 px-2 text-xs"
            >
              Restore
            </Button>
          </div>
        )}
      </div>

      <div
        className={cn(
          "hidden text-xs text-muted-foreground items-center gap-2 shrink-0",
          STATUS_BAR_WIDE_CQ + ":flex",
        )}
      >
        <DocumentStats
          contentLineCount={contentLineCount}
          contentCharCount={contentCharCount}
        />
      </div>

      <div className={cn("flex items-center gap-1.5 shrink-0", STATUS_BAR_WIDE_CQ + ":hidden")}>
        {driveSyncActivity === "working" && (
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-primary"
            aria-label={driveSyncLabel || "Syncing with Drive"}
          />
        )}
        {driveSyncActivity === "success" && (
          <CheckCircle2
            className="h-3.5 w-3.5 text-green-600 dark:text-green-500"
            aria-label={driveSyncLabel || "Synced"}
          />
        )}
        {driveSyncActivity === "conflict" && (
          <AlertTriangle
            className="h-3.5 w-3.5 text-amber-600 dark:text-amber-500"
            aria-label={driveSyncLabel || "Drive conflict"}
          />
        )}
        {driveSyncActivity === "error" && (
          <XCircle
            className="h-3.5 w-3.5 text-destructive"
            aria-label={driveSyncLabel || "Drive sync failed"}
          />
        )}
        {localSaving && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" aria-label="Saving" />
        )}
        {localIsDeleted && (
          <Button
            onClick={onRestore}
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs"
          >
            Restore
          </Button>
        )}
        {!infoTips && !localSaving && driveSyncActivity !== "working" && infoButton}
      </div>

      {infoTips ? infoButton : null}

      {error && (
        <div className={cn("hidden text-red-600 text-xs shrink-0", STATUS_BAR_WIDE_CQ + ":block")}>
          {error}
        </div>
      )}
    </div>
  )
}
